import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useRegister } from '../hooks/useAuth';
import { Button, Input, Card } from '../components';
import { Mail, Lock, User, Phone, BookOpen } from 'lucide-react';

type RegisterFormData = {
  email: string;
  password: string;
  confirmPassword: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: 'professional' | 'opdrachtgever';
};

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const registerMutation = useRegister();
  const { t } = useTranslation();

  const registerSchema = z.object({
    email: z.string().email(t('validation.invalidEmail')),
    password: z.string().min(8, t('validation.passwordMin8')),
    confirmPassword: z.string(),
    first_name: z.string().trim().min(2, t('validation.firstNameRequired')),
    last_name: z.string().trim().min(2, t('validation.lastNameRequired')),
    phone: z.string().optional(),
    role: z.enum(['professional', 'opdrachtgever'], {
      errorMap: () => ({ message: t('validation.selectRole') }),
    }),
  }).refine((data) => data.password === data.confirmPassword, {
    message: t('validation.passwordsMismatch'),
    path: ['confirmPassword'],
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
  });

  const selectedRole = watch('role');

  const onSubmit = (data: RegisterFormData) => {
    console.log('Submitting register data', data);
    const { confirmPassword, ...submitData } = data;
    registerMutation.mutate(submitData, {
      onSuccess: () => {
        // Redirect professionals to screening wizard, opdrachtgevers to onboarding wizard
        if (data.role === 'professional') {
          navigate('/professional/screening');
        } else if (data.role === 'opdrachtgever') {
          navigate('/employer/onboarding');
        } else {
          navigate('/dashboard');
        }
      },
    });
  };

  const backendErrors = (registerMutation.error as any)?.response?.data?.errors as string[] | undefined;

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-md w-full">
        {/* Logo + uitleg */}
        <div className="text-center mb-8">
          <img
            src="/belagom-text.png"
            alt="BeLagom"
            className="h-10 mx-auto mb-4 cursor-pointer"
            onClick={() => navigate('/')}
          />
          <h2 className="text-3xl font-bold text-gray-900">{t('register.title')}</h2>
          <p className="mt-2 text-gray-600">
            {t('register.description')}
          </p>
          <p className="mt-2 text-gray-600">
            {t('register.hasAccount')}{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-500 font-medium">
              {t('register.loginHere')}
            </Link>
          </p>
        </div>

        <Card>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {backendErrors && backendErrors.length > 0 && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 space-y-1">
                {backendErrors.map((err, i) => (
                  <p key={i}>{err}</p>
                ))}
              </div>
            )}
            {import.meta.env.MODE !== 'production' && (
              <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                {JSON.stringify({
                  first_name: watch('first_name'),
                  last_name: watch('last_name'),
                  role: watch('role'),
                  email: watch('email')
                }, null, 2)}
              </pre>
            )}
            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('register.roleLabel')}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
                  selectedRole === 'professional'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300'
                }`}>
                  <input
                    type="radio"
                    value="professional"
                    className="sr-only"
                    {...register('role')}
                  />
                  <div className="flex flex-1 flex-col">
                    <span className="block text-sm font-medium text-gray-900">
                      {t('register.professional')}
                    </span>
                    <span className="mt-1 text-xs text-gray-500">
                      {t('register.professionalDesc')}
                    </span>
                  </div>
                </label>

                <label className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
                  selectedRole === 'opdrachtgever'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300'
                }`}>
                  <input
                    type="radio"
                    value="opdrachtgever"
                    className="sr-only"
                    {...register('role')}
                  />
                  <div className="flex flex-1 flex-col">
                    <span className="block text-sm font-medium text-gray-900">
                      {t('register.employer')}
                    </span>
                    <span className="mt-1 text-xs text-gray-500">
                      {t('register.employerDesc')}
                    </span>
                  </div>
                </label>
              </div>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
              )}

              <div className="mt-3">
                <Link
                  to="/opleider/register"
                  className="flex items-center gap-3 rounded-lg border border-gray-300 p-4 hover:border-blue-400 hover:bg-blue-50 transition-colors group"
                >
                  <BookOpen className="w-5 h-5 text-gray-400 group-hover:text-blue-500 flex-shrink-0" />
                  <div>
                    <span className="block text-sm font-medium text-gray-900">{t('register.trainingProvider')}</span>
                    <span className="text-xs text-gray-500">{t('register.trainingProviderDesc')}</span>
                  </div>
                  <span className="ml-auto text-xs text-blue-600 font-medium group-hover:underline">{t('register.trainingProviderLink')}</span>
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('register.firstName')}
                icon={<User />}
                error={errors.first_name?.message}
                {...register('first_name')}
              />

              <Input
                label={t('register.lastName')}
                icon={<User />}
                error={errors.last_name?.message}
                {...register('last_name')}
              />
            </div>

            <Input
              label={t('register.email')}
              type="email"
              icon={<Mail />}
              error={errors.email?.message}
              {...register('email')}
            />

            <Input
              label={t('register.phone')}
              type="tel"
              icon={<Phone />}
              error={errors.phone?.message}
              {...register('phone')}
            />

            <Input
              label={t('register.password')}
              type="password"
              icon={<Lock />}
              error={errors.password?.message}
              {...register('password')}
            />

            <Input
              label={t('register.confirmPassword')}
              type="password"
              icon={<Lock />}
              error={errors.confirmPassword?.message}
              {...register('confirmPassword')}
            />

            <div className="flex items-center">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                required
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="terms" className="ml-2 block text-sm text-gray-900">
                {t('register.agreeTermsPrefix')}{' '}
                <Link to="/terms" className="text-blue-600 hover:text-blue-500">
                  {t('register.terms')}
                </Link>{' '}
                {t('register.and')}{' '}
                <Link to="/privacy" className="text-blue-600 hover:text-blue-500">
                  {t('register.privacy')}
                </Link>
              </label>
            </div>

            <Button
              type="submit"
              className="w-full"
              loading={registerMutation.isPending}
            >
              {t('register.submit')}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};
